/**
 * DocumentsShell — shared page wrapper for `/documents` and `/documents/:id`.
 *
 * Renders:
 *   - `<AppNav />`
 *   - Site-wide compliance notice (from `site-settings`)
 *   - Private-storage disclaimer (user-approved, verbatim, inline)
 *
 * Layout parity target: `GrantShell.jsx`. Above-the-fold budget < 200px so
 * category tabs and the first two rows of the card grid render within the
 * fold on 1366×768 even when the unverified-email banner is present.
 */
import { useEffect, useState } from "react";
import { Info, ShieldCheck } from "lucide-react";
import AppNav from "@/components/app/AppNav";
import { api } from "@/api/client";

export default function DocumentsShell({ children, testId = "documents-shell-root" }) {
  const [siteSettings, setSiteSettings] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api
      .get("/site-settings")
      .then((res) => {
        if (!cancelled) setSiteSettings(res.data);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const disclaimer = siteSettings?.disclaimer_text;

  return (
    <div
      data-testid={testId}
      data-page-outer="documents"
      className="min-h-screen text-v2-text bg-v2-background"
      style={{ background: "var(--v2-background)" }}
    >
      <AppNav />
      <main className="max-w-6xl mx-auto px-6 md:px-10 pt-5 pb-24">
        {disclaimer && (
          <details
            data-testid="documents-site-disclaimer"
            className="rounded-xl px-3 py-2 text-[11px] text-v2-textMuted leading-relaxed"
            style={{
              background: "rgba(167,139,250,0.05)",
              border: "1px solid rgba(167,139,250,0.15)",
            }}
          >
            <summary className="flex items-center gap-2 cursor-pointer list-none">
              <Info className="w-3 h-3 text-v2-accent flex-shrink-0" />
              <span className="uppercase tracking-[0.14em] text-[10px] font-semibold text-v2-textMuted">
                Compliance notice
              </span>
              <span className="ml-1 text-v2-textMuted/70">— tap to expand</span>
            </summary>
            <p className="mt-2 pl-5">{disclaimer}</p>
          </details>
        )}
        <div
          data-testid="documents-private-storage-notice"
          className="mt-3 rounded-xl px-3 py-2 flex items-start gap-2 text-[11px] text-v2-textMuted leading-relaxed"
          style={{
            background: "rgba(148,163,184,0.05)",
            border: "1px solid rgba(148,163,184,0.15)",
          }}
        >
          <ShieldCheck className="w-3.5 h-3.5 text-v2-accent flex-shrink-0 mt-0.5" />
          <p>
            Uploaded documents are stored privately and are not shared with
            grant sponsors, funders, or third parties unless you explicitly
            submit them.
          </p>
        </div>
        {children}
      </main>
    </div>
  );
}
